import React, { useCallback, useState, useEffect } from 'react';
import { shallowEqual } from 'react-redux';
import createComponent, { RuleStyles, ComponentRuleProps } from 'styles/fela/createComponent';
import { useFela } from 'react-fela';
import { useI18n } from 'components/I18n';
import { useOnlineStatus } from 'utils/hooks';
import { useTranslatedPlayerMessages } from 'components/Player/utils';
import { useDispatch, useSelector } from 'reducers';
import storage from 'utils/storage';
import type MagineMediaPlayer from '@tvoli/hipster-player';
import logger from 'utils/logger';
import { addMessage } from 'actions/messages';

const VideoContainer = createComponent(({ theme }: ComponentRuleProps): RuleStyles => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',

  zIndex: theme.zIndex.trailerPlayer,

  ':fullscreen': {
    height: '100%',
    maxHeight: '100%',
  },
}), 'div');

VideoContainer.displayName = 'VideoContainer';

interface ITrailerPlayerProps {
  title: string;
  trailerUrl?: string;
  trailers: {
    id: string;
    defaultPlayable: {
      id: string;
    }
  }[];
  onClose: () => void;
}

export default function TrailerPlayer(props: ITrailerPlayerProps): JSX.Element {
  const { title, trailerUrl, trailers, onClose } = props;
  const { theme, renderer: felaRenderer } = useFela();
  const [player, setPlayer] = useState<MagineMediaPlayer | null>(null);
  const {
    apiBaseUri,
    language,
    clientApiToken,
    sessionToken,
    defaultAudioTrack,
    muxKey,
    userId,
    partner,
    brandName,
    version,
  } = useSelector(state => ({
    apiBaseUri: state.settings.apiBaseUri,
    language: state.settings.l10n.language,
    clientApiToken: state.settings.clientApiToken,
    sessionToken: state.auth.sessionToken,
    defaultAudioTrack: state.settings.features.player.defaultAudioTrack,
    muxKey: state.settings.features.mux?.id,
    userId: state.auth.userId,
    partner: state.settings.partner,
    brandName: state.settings.brand.name,
    version: state.settings.app.version,
  }), shallowEqual);

  const messages = useTranslatedPlayerMessages(language);

  const [preOfflinePosition, setPreOfflinePosition] = React.useState<number | undefined>(undefined);
  const [preOfflineIsPlaying, setPreOfflineIsPlaying] = React.useState(false);
  const isOnline = useOnlineStatus();
  const i18n = useI18n();

  const isHosted = !trailers?.[0];
  const dispatch = useDispatch();

  const playLinked = (viewableId: string, playableId: string) => {
    void player?.loadWithPreflight({
      accessToken: clientApiToken,
      ads: false,
      assetId: playableId,
      viewableId,
      viewable: {
        id: viewableId,
        title: i18n.formatText('trailer.title', { title }),
      },
      authorizationKey: sessionToken,
      playbackStartTime: 0,
    });
  };

  const playHosted = (playlist: string) => {
    void player?.playHostedVideo({
      playlist,
      assetId: playlist,
      viewableId: playlist,
      ads: false,
      viewable: {
        id: playlist,
        title: i18n.formatText('trailer.title', { title }),
      },
      playbackStartTime: 0,
    });
  };

  const handleClose = () => {
    onClose();
  };

  const handlePlayerError = (error: any) => {
    logger.warn('Got Hipster Player error', error);
    handleClose();

    const {
      playerError,
    } = player?.model || {};
    const { errorMessage, code } = playerError || {};
    const message = [];

    message.push(i18n.formatText(errorMessage.message, errorMessage.values));
    if (code) message.push(i18n.formatText('error.code', { code }));

    dispatch(addMessage({ contentId: message.join(' ') }));
  };

  const playTrailers = useCallback( () => {
    const trailer = trailers?.[0];

    if (trailer) {
      playLinked(
        trailer.id,
        trailer.defaultPlayable.id,
      );
    } else if (trailerUrl) {
      playHosted(trailerUrl);
    }
  }, [player]);

  const initPlayer = (node: HTMLElement) => {
    if (player) {
      return player;
    }

    // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
    const HipsterPlayer = require('@tvoli/hipster-player').default;

    const hipsterPlayer = new HipsterPlayer({
      baseUri: apiBaseUri,
      logLevel: __PRODUCTION__ ? 1 : 10,
      preferredAudio: storage.getItem('audioTrack') || defaultAudioTrack,
      playerContainer: node,
      muxKey,
      userId,
      partner,
      brandName,
      clientApplicationName: 'web-mamo',
      clientApplicationVersion: `web-mamo ${version}`,
      disableServices: {
        heartbeat: isHosted,
        telemetry: isHosted,
        chromecast: isHosted,
        dai: true,
        aspectratio: true,
      },
      ...(isHosted ? { type: 'hosted' } : null),
    });

    hipsterPlayer.renderControls({
      language,
      messages,
      theme,
      felaRenderer,
      hide: {
        audio: isHosted,
        quality: isHosted,
        cast: isHosted,
      },
    });


    setPlayer(hipsterPlayer);

    return hipsterPlayer;
  };

  useEffect(() => {
    if (player) {
      void playTrailers();

      player.addEventListener('exitFullWindow', handleClose);
      player.addEventListener('streamEnded', handleClose);
      player.addEventListener('error', handlePlayerError);

      return () => {
        player?.removeEventListener('exitFullWindow', handleClose);
        player?.removeEventListener('streamEnded', handleClose);
        player?.removeEventListener('error', handlePlayerError);
        setTimeout(() => void player?.destroy());
      };
    }

    return () => {};
  }, [player]);

  useEffect(() => {
    if (!player) {
      return;
    }
    if (!isOnline && !preOfflinePosition) {
      void player.pause();
      setPreOfflinePosition(player.model?.currentTime);
      setPreOfflineIsPlaying(player.model?.isPlaying);
    }
    if (isOnline && preOfflinePosition) {
      void player.seek(preOfflinePosition).then(() => {
        if (preOfflineIsPlaying) {
          void player.play();
        }
      });
      setPreOfflinePosition(undefined);
    }
  }, [isOnline, preOfflinePosition, preOfflineIsPlaying]);

  const playerContainerRef = useCallback((node: HTMLElement) => {
    if (node) {
      initPlayer(node);
      // Focus the container immediately so keyboard hotkeys (Space, M, etc.)
      // are active without requiring an extra click.
      node.focus();
    }
  }, []);

  return (
    <VideoContainer data-nosnippet tabIndex={-1} innerRef={playerContainerRef} />
  );
}
